import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@helsenorge/designsystem-react/components/Button';
import FormGroup from '@helsenorge/designsystem-react/components/FormGroup';
import Label, { Sublabel } from '@helsenorge/designsystem-react/components/Label';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import TextArea from '@helsenorge/designsystem-react/components/Textarea';
import Validation from '@helsenorge/designsystem-react/components/Validation';
import { debounce } from '@helsenorge/designsystem-react/utils/debounce';
import { error as logError } from '@helsenorge/framework-utils/logger';
import ScoreInput from './score-input';
import { scoreValueList } from './utils/entities';
import { trackComment, trackScore } from '../feedback/utils/analytics';
import * as api from '../feedback/utils/api';
import styles from './styles.module.scss';
export const CHARACTER_LIMIT = 250;
export const TEXTAREA_ID = 'textarea-description';
export const DESCRIPTION_ID = 'feedback-description';
const FeedbackForm = ({ url, pageTitle, resources }) => {
    const { register, handleSubmit, watch, setError, formState: { errors, isSubmitting, isSubmitSuccessful }, } = useForm();
    const [id, setId] = useState();
    const score = watch('score');
    const hasFeedbackId = id !== undefined;
    useEffect(() => {
        if (!score) {
            return;
        }
        const handleUpdate = async () => {
            if (id) {
                updateFeedback({ id, score });
                trackScore(score);
            }
        };
        const [debouncedUpdate, teardown] = debounce(handleUpdate, 1000);
        if (id) {
            debouncedUpdate();
        }
        else {
            createFeedback({ score, url: url, pageTitle: pageTitle });
        }
        return () => {
            teardown();
        };
    }, [score]);
    const createFeedback = async (data) => {
        try {
            const createFeedbackResponse = await api.createFeedback(data);
            setId(createFeedbackResponse?.id);
            trackScore(data.score);
        }
        catch {
            setError('root', { message: resources.serverErrorMessage });
        }
    };
    const updateFeedback = async (data) => {
        try {
            if (id) {
                await api.updateFeedback(data);
                if (data.comment) {
                    trackComment();
                }
            }
        }
        catch (e) {
            setError('root', { message: resources.serverErrorMessage });
            throw e;
        }
    };
    if (isSubmitSuccessful) {
        return _jsx(NotificationPanel, { role: "alert", label: resources.successMessage, variant: "success", compactVariant: "basic", size: "medium" });
    }
    if (errors.root) {
        return _jsx(NotificationPanel, { label: resources.serverErrorMessage, variant: "error", compactVariant: "basic", size: "medium" });
    }
    const getButtonAriaLabel = (value) => {
        switch (value) {
            case '5':
                return resources.veryPleasedText;
            case '4':
                return resources.slightlyPleasedText;
            case '3':
                return resources.neutralText;
            case '2':
                return resources.slightlyDispleasedText;
            case '1':
                return resources.displeasedText;
        }
    };
    const onSubmit = async (data) => {
        if (!id || id === undefined) {
            logError('Skjema for tilbakemelding fra bruker ble forsøkt sendt inn uten id.');
            setError('root', { message: resources.serverErrorMessage });
        }
        try {
            await api.updateFeedback({
                id: id,
                score: data.score,
                comment: data.comment,
            });
            if (data.comment) {
                trackComment();
            }
        }
        catch {
            setError('root', { message: resources.serverErrorMessage });
        }
    };
    return (_jsx(Validation, { errorTitle: resources.serverErrorMessage, children: _jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [_jsx(FormGroup, { legend: resources.questionText, fieldsetClassName: styles.feedbackform__questionwrapper, children: _jsx("div", { className: styles.feedbackform__scores, children: scoreValueList.map(value => (_jsx(ScoreInput, { label: getButtonAriaLabel(value), value: value, selected: score === value, disabled: isSubmitting, expanded: hasFeedbackId, ...register('score') }, value))) }) }), hasFeedbackId && (_jsx(FormGroup, { error: errors?.comment ? errors?.comment.message : undefined, children: _jsx(TextArea, { label: _jsx(Label, { htmlFor: TEXTAREA_ID, className: styles.feedbackform__commentlabel, labelTexts: [{ text: resources.commentLabel }, { text: resources.commentSubLabel }], sublabel: _jsx(Sublabel, { id: DESCRIPTION_ID, sublabelTexts: [{ text: resources.commentDescription, type: 'subdued' }] }) }), textareaId: TEXTAREA_ID, "aria-describedby": DESCRIPTION_ID, maxCharacters: CHARACTER_LIMIT, maxText: resources.maxText, width: 48, disabled: isSubmitting, ...register('comment', { maxLength: { value: CHARACTER_LIMIT, message: resources.commentTooLongErrorMessage } }) }) })), _jsx(Spacer, { size: "m" }), hasFeedbackId && (_jsx(Button, { type: "submit", disabled: isSubmitting, className: styles.feedbackform__button, children: resources.submitButtonText }))] }) }));
};
const Feedback = ({ ...props }) => (_jsx("div", { className: styles.feedbackform, children: _jsx(FeedbackForm, { ...props }) }));
export default Feedback;
